import type { IRecordTabsOverride, IRecordTypeDefinition, IRecordTypeOverride, StandardViewKey } from '@platform/core/types';
import { standardViewKeys } from './builderViewKeys';
import type { IBuilderViews } from './IBuilderViews';

/** Подання, увімкнені для типу з урахуванням патча (патч — важливіший за злите визначення, поки зміна ще зберігається). */
export function viewsOf(rt: IRecordTypeDefinition, patch: IRecordTypeOverride): IBuilderViews {
  const enabled = patch.views?.enabled ?? rt.views.enabled;
  const custom = patch.views?.custom ?? rt.views.custom ?? [];
  return { enabled: [...enabled], custom: [...custom], def: patch.views?.default ?? rt.views.default };
}

/**
 * Увімкнути чи вимкнути подання (прототип v4 bViews.toggle): стандартне — у `views.enabled`, власне — у `views.custom`; останнє увімкнене
 * подання не вимикається; типове подання, якщо його вимкнено, стає першим увімкненим. Чиста функція.
 */
export function toggleView(patch: IRecordTypeOverride, rt: IRecordTypeDefinition, key: string, on: boolean): IRecordTypeOverride {
  const cur = viewsOf(rt, patch);
  const standard = (standardViewKeys as readonly string[]).includes(key);
  const list = standard ? cur.enabled : cur.custom;
  const next = on ? [...new Set([...list, key])] : list.filter((k) => k !== key);
  const enabled = standard ? standardViewKeys.filter((k) => next.includes(k)) : cur.enabled;
  const custom = standard ? cur.custom : next;
  if (!enabled.length && !custom.length) return patch;
  const views = { ...(patch.views ?? {}), ...(standard ? { enabled: enabled as StandardViewKey[] } : { custom }) };
  if (![...enabled, ...custom].includes(cur.def)) views.default = enabled[0] ?? custom[0]!;
  return { ...patch, views };
}

/** Типове подання (`views.default`). Чиста функція. */
export function setDefaultView(patch: IRecordTypeOverride, key: string): IRecordTypeOverride {
  return { ...patch, views: { ...(patch.views ?? {}), default: key } };
}

/** Поле групування беклогу (прототип v4 groupBy): `views.settings.backlog.groupBy` і групування дошки. Чиста функція. */
export function setBacklogGroupBy(patch: IRecordTypeOverride, key: string): IRecordTypeOverride {
  const settings = { ...(patch.views?.settings ?? {}) };
  settings.backlog = { ...(settings.backlog ?? {}), groupBy: key };
  return { ...patch, views: { ...(patch.views ?? {}), boardGroupBy: key, settings } };
}

/** Вкладка форми «Пов’язані елементи» чи «Історія» (ADR-023 `tabs`). Чиста функція. */
export function setTab(patch: IRecordTypeOverride, tab: keyof IRecordTabsOverride, on: boolean): IRecordTypeOverride {
  return { ...patch, tabs: { ...(patch.tabs ?? {}), [tab]: on } };
}

/** Чи вкладка є у формі типу з урахуванням патча. */
export function tabOn(rt: IRecordTypeDefinition, patch: IRecordTypeOverride, tab: keyof IRecordTabsOverride): boolean {
  return patch.tabs?.[tab] ?? (rt.forms?.full ?? rt.form).tabs.includes(tab);
}
